import { createContext, forwardRef, useContext, useMemo, useState } from 'react';
import { Platform, Pressable as RnPressable, StyleSheet, View } from 'react-native';
import type { PressableProps as RnPressableProps, StyleProp, ViewStyle } from 'react-native';

import { useMergedEventHandlers } from '@fhs/utils';

import { tokens } from '../../tokens';

import { DEFAULT_PRESSABLE_BORDER_RADIUS, PRESSABLE_BORDER_WIDTH } from './constants';
import { PressableOutline } from './pressable-outline';

export type PressableState = {
  pressed: boolean;
  hovered: boolean;
  focused: boolean;
  disabled: boolean;
  softDisabled?: boolean;
};

const PressableContext = createContext<PressableState>({
  pressed: false,
  hovered: false,
  focused: false,
  disabled: false,
  softDisabled: false,
});

export function usePressableState() {
  return useContext(PressableContext);
}

export type PressableProps = Omit<RnPressableProps, 'style'> & {
  style?: StyleProp<ViewStyle>;
  pressedStyle?: StyleProp<ViewStyle>;
  hoveredStyle?: StyleProp<ViewStyle>;
  focusedStyle?: StyleProp<ViewStyle>;
  disabledStyle?: StyleProp<ViewStyle>;
  softDisabledStyle?: StyleProp<ViewStyle>;

  outlineStyle?: StyleProp<ViewStyle>;
  outlinePressedStyle?: StyleProp<ViewStyle>;
  outlineHoveredStyle?: StyleProp<ViewStyle>;
  outlineFocusedStyle?: StyleProp<ViewStyle>;
  outlineDisabledStyle?: StyleProp<ViewStyle>;

  borderWidth?: number;
  borderRadius?: number;

  borderTopLeftRadius?: number;
  borderTopRightRadius?: number;
  borderBottomRightRadius?: number;
  borderBottomLeftRadius?: number;

  softDisabled?: boolean;
};

export const Pressable = forwardRef<View, PressableProps>(function Pressable(
  {
    children,
    borderRadius = DEFAULT_PRESSABLE_BORDER_RADIUS,

    borderTopLeftRadius = borderRadius,
    borderTopRightRadius = borderRadius,
    borderBottomRightRadius = borderRadius,
    borderBottomLeftRadius = borderRadius,

    borderWidth = PRESSABLE_BORDER_WIDTH,

    // this should not get forwarded to underlying view
    outlineStyle,
    ...props
  }: PressableProps,
  forwardedRef
) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const disabled = props.disabled ?? false;
  const softDisabled = props.softDisabled ?? false;

  const pressableState: PressableState = useMemo(
    () => ({ pressed, hovered, focused, disabled, softDisabled }),
    [pressed, hovered, focused, disabled, softDisabled]
  );

  const handleOnPressIn = useMergedEventHandlers(props.onPressIn, () => setPressed(true));
  const handleOnPressOut = useMergedEventHandlers(props.onPressOut, () => setPressed(false));

  const handleOnHoverIn = useMergedEventHandlers(props.onHoverIn, () => setHovered(true));
  const handleOnHoverOut = useMergedEventHandlers(props.onHoverOut, () => setHovered(false));

  const handleOnFocus = useMergedEventHandlers(props.onFocus, () => setFocused(true));
  const handleOnBlur = useMergedEventHandlers(props.onBlur, () => setFocused(false));

  return (
    <RnPressable
      ref={forwardedRef}
      {...props}
      disabled={disabled}
      onPressIn={handleOnPressIn}
      onPressOut={handleOnPressOut}
      onHoverIn={handleOnHoverIn}
      onHoverOut={handleOnHoverOut}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      style={[
        styles.baseline,
        {
          borderTopLeftRadius,
          borderTopRightRadius,
          borderBottomRightRadius,
          borderBottomLeftRadius,
          borderWidth,
        },
        props.style,
        pressed && !softDisabled && props.pressedStyle,
        focused && !softDisabled && props.focusedStyle,
        hovered && !softDisabled && props.hoveredStyle,
        disabled && !softDisabled && [styles.disabled, props.disabledStyle],
      ]}
    >
      <PressableContext.Provider value={pressableState}>
        {typeof children === 'function' ? children(pressableState) : children}
        <PressableOutline
          borderWidth={borderWidth}
          borderTopLeftRadius={borderTopLeftRadius}
          borderTopRightRadius={borderTopRightRadius}
          borderBottomRightRadius={borderBottomRightRadius}
          borderBottomLeftRadius={borderBottomLeftRadius}
          style={[
            outlineStyle,
            pressed && !softDisabled && [(outlineStyles.pressed, props.outlinePressedStyle)],
            focused && !softDisabled && [outlineStyles.focused, props.outlineFocusedStyle],
            hovered && !softDisabled && [outlineStyles.hovered, props.outlineHoveredStyle],
            disabled && !softDisabled && [outlineStyles.disabled, props.outlineDisabledStyle],
            softDisabled && [outlineStyles.disabled, props.softDisabled],
          ]}
        />
      </PressableContext.Provider>
    </RnPressable>
  );
});

const styles = StyleSheet.create({
  baseline: {
    ...Platform.select({ web: { cursor: 'pointer' } }),
    borderColor: 'transparent',
    // relative position required for Outline
    position: 'relative',
    ...Platform.select({
      // remove outline on web
      web: { outlineWidth: 0 },
    }),
  },

  disabled: {
    // web allows this, but react native changed their types
    ...(Platform.select({ web: { cursor: 'not-allowed' } }) as any),
    pointerEvents: 'none',
  },
  softDisabled: {
    ...(Platform.select({ web: { cursor: 'not-allowed' } }) as any),
  },
});

const outlineStyles = StyleSheet.create({
  pressed: {
    borderColor: tokens.colors.$blackOpacity10,
  },
  hovered: {},
  focused: {
    borderColor: tokens.colors.$blackOpacity10,
  },
  disabled: {
    borderColor: tokens.colors.$transparent,
  },
});
